import React, { useRef, useCallback, useEffect, useState } from "react";
import { useCanvasLoop } from "../shared/useCanvasLoop";
import { mulberry32 } from "@/lib/simulation/rng";
import { ambientSound } from "@/lib/simulation/sound";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
import { Volume2, VolumeX, FastForward, ArrowRight } from "lucide-react";
export interface OpeningProps {
  onEnter: () => void;
}
interface Particle {
  x: number;
  y: number;
  z: number;
  size: number;
  alpha: number;
  twinkleSpeed: number;
}
interface MathGlyph {
  text: string;
  x: number;
  y: number;
  size: number;
  alpha: number;
}
const STATEMENTS = ["REALITY IS ALL YOU HAVE EVER KNOWN.", "BUT WHAT IF REALITY IS NOT WHAT YOU THINK IT IS?", "WOULD YOU KNOW IF YOUR UNIVERSE WERE SIMULATED?"] as const;
export default function Opening({
  onEnter
}: OpeningProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const enterBtnRef = useRef<HTMLButtonElement | null>(null);
  const prefersReduced = useReducedMotion();

  // Animation timeline state: 0 -> 1 -> 2 -> 3 (all visible + CTA ready)
  // Under prefers-reduced-motion, jump directly to 3
  const [stage, setStage] = useState<number>(() => prefersReduced ? 3 : 0);
  const [isExiting, setIsExiting] = useState(false);
  const [soundActive, setSoundActive] = useState(false);
  const [soundSupported, setSoundSupported] = useState(false);
  useEffect(() => {
    setSoundSupported(ambientSound.isSupported());
  }, []);

  // Progression of the 3 statements
  useEffect(() => {
    if (prefersReduced) {
      setStage(3);
      return;
    }

    // Stage 0: 0s - 1.2s: light point appears, stars emerge
    const t0 = setTimeout(() => {
      setStage(1); // First statement appears
    }, 1200);

    // Stage 1: ~3.0s later
    const t1 = setTimeout(() => {
      setStage(2); // Second statement appears
    }, 4200);

    // Stage 2: ~3.0s later
    const t2 = setTimeout(() => {
      setStage(3); // Third statement appears + ENTER button
    }, 7200);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [prefersReduced]);
  const handleSkip = useCallback(() => {
    setStage(3);
    setTimeout(() => {
      enterBtnRef.current?.focus();
    }, 50);
  }, []);
  const handleToggleSound = useCallback(() => {
    const next = ambientSound.toggle();
    setSoundActive(next);
  }, []);
  const handleEnterClick = useCallback(() => {
    if (prefersReduced) {
      onEnter();
      return;
    }
    setIsExiting(true);
    setTimeout(() => {
      onEnter();
    }, 1100);
  }, [onEnter, prefersReduced]);

  // Seeded particles & math notation layout
  const entitiesRef = useRef<{
    particles: Particle[];
    glyphs: MathGlyph[];
    initialized: boolean;
  }>({
    particles: [],
    glyphs: [],
    initialized: false
  });
  const initEntities = useCallback((w: number, h: number) => {
    const rng = mulberry32(42179);
    const particleCount = w < 768 ? 90 : 180;
    const particles: Particle[] = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: (rng() - 0.5) * w * 1.5,
        y: (rng() - 0.5) * h * 1.5,
        z: rng() * 1000 + 100,
        size: 0.8 + rng() * 1.8,
        alpha: 0.2 + rng() * 0.8,
        twinkleSpeed: 0.001 + rng() * 0.003
      });
    }
    const mathSymbols = ["ψ", "∇²", "E = mc²", "ħ", "S = k ln W", "1 0 1 1 0", "iℏ∂/∂t", "c = 299792458", "λ = h/p"];
    const glyphs: MathGlyph[] = [];
    const glyphCount = w < 768 ? 8 : 16;
    for (let i = 0; i < glyphCount; i++) {
      glyphs.push({
        text: mathSymbols[Math.floor(rng() * mathSymbols.length)],
        x: rng() * w,
        y: rng() * h,
        size: 11 + Math.floor(rng() * 6),
        alpha: 0.15 + rng() * 0.25
      });
    }
    entitiesRef.current = {
      particles,
      glyphs,
      initialized: true
    };
  }, []);
  const draw = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number, timeMs: number) => {
    if (!entitiesRef.current.initialized) {
      initEntities(w, h);
    }
    ctx.clearRect(0, 0, w, h);

    // Deep space void ground
    ctx.fillStyle = "#05060f";
    ctx.fillRect(0, 0, w, h);

    // Intro progress factor: 0 -> 1 over 6 seconds
    const introFactor = prefersReduced ? 1 : Math.min(1, Math.max(0, timeMs / 5500));
    const cx = w / 2;
    const cy = h / 2;

    // 1. Perspective grid extending to infinity
    const gridAlpha = 0.18 * introFactor;
    if (gridAlpha > 0.01) {
      ctx.save();
      ctx.strokeStyle = `rgba(0, 229, 255, ${gridAlpha})`;
      ctx.lineWidth = 1;
      const horizonY = cy + h * 0.08;
      const gridBottom = h;
      const vanishX = cx;

      // Radiating perspective lines
      const linesCount = w < 768 ? 12 : 20;
      for (let i = -linesCount / 2; i <= linesCount / 2; i++) {
        const spread = w * 1.8 * i / (linesCount / 2);
        ctx.beginPath();
        ctx.moveTo(vanishX, horizonY);
        ctx.lineTo(cx + spread, gridBottom);
        ctx.stroke();
      }

      // Horizontal cross lines moving toward viewer
      const horizLines = 9;
      const speedOffset = prefersReduced ? 0 : timeMs * 0.0003 % 1;
      for (let i = 1; i <= horizLines; i++) {
        const norm = Math.pow((i + speedOffset) / (horizLines + 1), 2.2);
        const y = horizonY + (gridBottom - horizonY) * norm;
        const halfWidth = w * 1.8 * norm / 2;
        ctx.beginPath();
        ctx.moveTo(cx - halfWidth, y);
        ctx.lineTo(cx + halfWidth, y);
        ctx.stroke();
      }
      ctx.restore();
    }

    // 2. Distant galaxies / particle field
    const {
      particles,
      glyphs
    } = entitiesRef.current;
    ctx.save();
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      const drift = prefersReduced ? 0 : timeMs * p.twinkleSpeed;
      const alpha = p.alpha * (0.6 + 0.4 * Math.sin(drift)) * introFactor;

      // Project particle from 3D coords
      const screenX = cx + p.x * 350 / p.z;
      const screenY = cy + p.y * 350 / p.z;
      if (screenX >= 0 && screenX <= w && screenY >= 0 && screenY <= h) {
        ctx.fillStyle = i % 4 === 0 ? `rgba(0, 229, 255, ${alpha})` : i % 5 === 0 ? `rgba(167, 139, 250, ${alpha})` : `rgba(230, 235, 245, ${alpha})`;
        ctx.beginPath();
        ctx.arc(screenX, screenY, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // 3. Mathematical notation floating faintly
    ctx.font = "12px JetBrains Mono, monospace";
    for (let i = 0; i < glyphs.length; i++) {
      const g = glyphs[i];
      const alpha = g.alpha * introFactor;
      ctx.fillStyle = `rgba(77, 124, 255, ${alpha * 0.7})`;
      ctx.fillText(g.text, g.x, g.y);
    }
    ctx.restore();

    // 4. Central point of light that emerges first
    const pulse = prefersReduced ? 1 : 0.85 + 0.15 * Math.sin(timeMs * 0.003);
    const centerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 140 * introFactor);
    centerGrad.addColorStop(0, `rgba(0, 229, 255, ${0.45 * pulse * introFactor})`);
    centerGrad.addColorStop(0.3, `rgba(77, 124, 255, ${0.2 * pulse * introFactor})`);
    centerGrad.addColorStop(0.7, `rgba(167, 139, 250, ${0.08 * introFactor})`);
    centerGrad.addColorStop(1, "rgba(5, 6, 15, 0)");
    ctx.fillStyle = centerGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, 140 * introFactor, 0, Math.PI * 2);
    ctx.fill();
  }, [initEntities, prefersReduced]);
  useCanvasLoop(canvasRef, draw, {
    running: true,
    maxDpr: 2
  });
  return <section aria-label="Simulation Theory Introduction" className={`relative w-full min-h-[100svh] flex flex-col justify-between items-center text-center px-4 py-8 select-none overflow-hidden transition-all duration-1000 ${isExiting ? "opacity-0 scale-105 pointer-events-none" : "opacity-100 scale-100"}`} style={{
    paddingTop: "calc(90px + 1.5rem)"
  }}>
      {/* Background canvas */}
      <canvas ref={canvasRef} aria-label="Cosmic starfield, perspective grid, and floating mathematical coordinates" className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      {/* Top utility controls: skip intro + sound toggle */}
      <header className="relative z-10 w-full max-w-5xl flex items-center justify-between text-xs font-mono text-muted-foreground">
        <div>
          <span className="inline-block uppercase tracking-[0.2em] text-primary/80 font-bold text-[0.7rem]">
            Quantum Liaison
          </span>
          <span className="hidden sm:inline mx-2 text-border">/</span>
          <span className="hidden sm:inline text-muted-foreground text-[0.7rem] uppercase tracking-wider">
            Theoretical Physics &amp; Computation
          </span>
        </div>

        <div className="flex items-center gap-3">
          {soundSupported && <button type="button" onClick={handleToggleSound} aria-pressed={soundActive} aria-label={soundActive ? "Sound on, click to mute" : "Sound off, click to unmute"} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card/60 backdrop-blur-md text-foreground hover:border-primary hover:text-primary transition-colors text-xs min-h-[44px]">
              {soundActive ? <Volume2 className="w-4 h-4 text-primary" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
              <span>{soundActive ? "Sound on" : "Sound off"}</span>
            </button>}

          {stage < 3 && !prefersReduced && <button type="button" onClick={handleSkip} aria-label="Skip introduction" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card/60 backdrop-blur-md text-muted-foreground hover:border-primary hover:text-foreground transition-colors text-xs min-h-[44px]">
              <span>Skip intro</span>
              <FastForward className="w-3.5 h-3.5" />
            </button>}
        </div>
      </header>

      {/* Center content: Title + sequential statements + CTA */}
      <div className="relative z-10 w-full max-w-4xl mx-auto my-auto flex flex-col items-center justify-center py-6">
        {/* Page small branding */}
        <div className="mb-4">
          <p className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-primary font-semibold">
            EXPERIMENT // HYPOTHESIS // SYNTHESIS
          </p>
          <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl font-extrabold tracking-wider text-foreground mt-2 text-transparent bg-clip-text bg-gradient-to-b from-white via-slate-100 to-slate-400">
            SIMULATION THEORY
          </h1>
          <p className="font-sans text-sm sm:text-base md:text-lg text-muted-foreground mt-2 tracking-wide">
            Question the Nature of Reality.
          </p>
        </div>

        {/* 3 Sequential statements in exact text and caps */}
        <div className="min-h-[14rem] sm:min-h-[11rem] flex flex-col items-center justify-center gap-4 my-6 w-full px-2" aria-live="polite">
          {/* Statement 1 */}
          <p className={`font-serif text-base sm:text-xl md:text-2xl font-bold tracking-wider text-slate-100 transition-all duration-700 max-w-2xl ${stage >= 1 || prefersReduced ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}`}>
            {STATEMENTS[0]}
          </p>

          {/* Statement 2 */}
          <p className={`font-serif text-base sm:text-xl md:text-2xl font-bold tracking-wider text-primary transition-all duration-700 max-w-2xl ${stage >= 2 || prefersReduced ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}`}>
            {STATEMENTS[1]}
          </p>

          {/* Statement 3 */}
          <p className={`font-serif text-base sm:text-xl md:text-2xl font-bold tracking-wider text-slate-200 transition-all duration-700 max-w-2xl ${stage >= 3 || prefersReduced ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}`}>
            {STATEMENTS[2]}
          </p>
        </div>

        {/* Prominent CTA */}
        <div className={`transition-all duration-500 mt-2 ${stage >= 3 || prefersReduced ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}`}>
          <button ref={enterBtnRef} type="button" onClick={handleEnterClick} className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-full font-serif font-bold text-sm sm:text-base tracking-[0.1em] uppercase text-primary-foreground bg-primary border border-primary hover:brightness-110 active:scale-98 transition-all min-h-[48px] shadow-[0_0_24px_rgba(0,229,255,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background">
            <span>ENTER THE INVESTIGATION</span>
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>

      {/* Bottom status note */}
      <footer className="relative z-10 w-full max-w-5xl flex flex-wrap items-center justify-between text-[0.75rem] font-mono text-muted-foreground/80 pt-4 border-t border-border/40">
        <p className="text-left">10 INTERACTIVE MISSIONS &bull; BROWSER-ONLY COMPUTATION</p>
        <p className="text-right">NO SERVER RECORDINGS &bull; FREE EDUCATIONAL LAB</p>
      </footer>
    </section>;
}